// #41 Checklist and CheckRow: gate and prerequisite lists (*Missing prerequisites*, *Drivers*). Each row is a 22 px
// glyph on its tone's tint (ok check · warn alert · todo clock · bad x), the words and an optional sub line. A row whose
// words are a deep link (H-7, H-8) takes `href` and is focusable on its link; `person` is the slot for #94's named
// person who ticks the row, `control` a trailing slot after it for that row's own control (#94's tick, a gate's
// action), and `count` a Count after the words (a gate row's open items). The glyph is decorative: `statusLabel` gives the row's state words to assistive
// technology when the words themselves do not carry it.

import type { MouseEvent, ReactNode } from "react";
import { cx } from "../lib/cx";
import { VisuallyHidden } from "../lib/VisuallyHidden";
import { Icon, type IconName } from "./icons";
import { Count } from "./Count";
import { TextLink } from "./Link";
import type { Hs } from "../lib/hs";

export type CheckStatus = "ok" | "warn" | "todo" | "bad";

const GLYPH: Readonly<Record<CheckStatus, IconName>> = { ok: "check", warn: "alert", todo: "clock", bad: "x" };

export interface ChecklistProps {
  children: ReactNode;
  /** Names the list (a section title's id). */
  labelledBy?: string | undefined;
  className?: string | undefined;
}

export function Checklist({ children, labelledBy, className }: ChecklistProps) {
  return <ul className={cx("mc-checks", className)} aria-labelledby={labelledBy}>{children}</ul>;
}

export interface CheckRowProps {
  status: CheckStatus;
  /** The row's words: a gate, a prerequisite, a driver. */
  children: ReactNode;
  sub?: ReactNode | undefined;
  /** The deep link to the exact screen that clears the row. */
  href?: string | undefined;
  onClick?: ((event: MouseEvent<HTMLElement>) => void) | undefined;
  /** The state in words, for assistive technology (the glyph is decorative). */
  statusLabel?: string | undefined;
  /** #94: the named person who ticks this row (a Person or Avatar, or their control). */
  person?: ReactNode | undefined;
  /** A trailing control after the person slot (#94's tick). */
  control?: ReactNode | undefined;
  /** A count after the words (a gate row's open items). */
  count?: number | undefined;
  hs?: Hs | undefined;
  className?: string | undefined;
}

export function CheckRow({ status, children, sub, href, onClick, statusLabel, person, control, count, hs, className }: CheckRowProps) {
  const words = href !== undefined || onClick !== undefined
    ? <TextLink href={href} onClick={onClick} hs={hs}>{children}</TextLink>
    : <span className="mc-chk-t">{children}</span>;
  return (
    <li className={cx("mc-chk", `mc-chk-${status}`, className)}>
      <span className="mc-ci" aria-hidden="true"><Icon name={GLYPH[status]} size={14} /></span>
      <span className="mc-cx">
        {statusLabel ? <VisuallyHidden>{statusLabel}</VisuallyHidden> : null}
        {words}
        {sub !== undefined && sub !== null ? <span className="mc-chk-s">{sub}</span> : null}
      </span>
      {count !== undefined ? <Count n={count} /> : null}
      {person ? <span className="mc-chk-p">{person}</span> : null}
      {control ? <span className="mc-chk-c">{control}</span> : null}
    </li>
  );
}
